import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Download, Gift as GiftIcon, Loader2, Lock } from "lucide-react";
import { Header } from "../components/Header";
import { trackGiftPurchase } from "../lib/analytics";
import { api, errMsg } from "../lib/api";
import { eur } from "../lib/shield";
import { usePrivatePage, useSeo, SITE_URL } from "../lib/seo";

const PRESETS = [20, 29, 39, 65];
const GIFT_LD = {
  "@context": "https://schema.org", "@type": "Product", name: "Sports Shields Gift Voucher", sku: "gift-voucher",
  description: "A gift voucher for a personalised player shield. Any amount from €20 to €200, comes as a printable gift card and never expires.",
  image: `${SITE_URL}/img/og-default.jpg`, brand: { "@type": "Brand", name: "sportsshields.ie" },
  offers: { "@type": "AggregateOffer", priceCurrency: "EUR", lowPrice: "20", highPrice: "200", offerCount: "1", availability: "https://schema.org/InStock", url: `${SITE_URL}/gift` },
};
const field = "mt-1 block w-full border-2 border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-slate-900";
const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0c2a1f]/[0.03]">
    <Header onNew={() => { window.location.href = "/design"; }} />
    <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:py-12">{children}</main>
  </div>
);

const Input = ({ label, testId, ...p }) => <label className="block text-xs font-bold">{label}<input className={field} data-testid={testId} {...p} /></label>;

export default function Gift() {
  useSeo({ title: "Player Shield Gift Vouchers | sportsshields.ie", description: "Buy a Sports Shields gift voucher from €20 to €200. They design their own personalised player shield – printable gift card, never expires.", path: "/gift", jsonLd: GIFT_LD });
  const [amount, setAmount] = useState(40);
  const [f, setF] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", recipient_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const valid = amount >= 20 && amount <= 200 && Number.isInteger(amount);
  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/vouchers", { ...f, recipient_email: f.recipient_email || null, amount, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <Shell>
      <div className="grid gap-10 lg:grid-cols-[1fr_420px]">
        <section>
          <div className="eyebrow">Christmas gift vouchers</div>
          <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl">Give a shield <span className="text-red-600">they'll design</span></h1>
          <p className="mt-4 max-w-lg text-slate-600">Not sure of their photo, club or size? Send a gift voucher. They design their own personalised player shield and enter the code at checkout.</p>
          <ul className="mt-6 space-y-2 text-sm font-semibold">{["Any amount from €20 to €200", "Printable gift card PDF – perfect for a stocking", "Never expires, and any unused balance stays on the code", "Optional: we'll email it straight to them"].map((t) => (
            <li key={t} className="flex gap-2"><CheckCircle2 className="h-5 w-5 shrink-0 text-lime-600" />{t}</li>))}</ul>
          <div className="relative mt-8 max-w-md overflow-hidden border-4 border-slate-900 bg-[#0c2a1f] p-4 text-white shadow-[8px_8px_0_0_#a3e635] sm:p-6" data-testid="gift-card-preview">
            <div className="absolute inset-y-0 right-14 w-5 bg-red-600" /><div className="absolute inset-x-0 top-1/2 h-5 bg-red-600" />
            <div className="relative bg-white p-5 text-slate-900">
              <div className="text-xs font-black uppercase text-lime-700">Sports Shields</div>
              <div className="font-display text-2xl font-black uppercase">Gift voucher</div>
              <div className="font-display mt-2 text-5xl font-black text-red-600" data-testid="gift-preview-amount">{valid ? eur(amount * 100).replace(".00", "") : "€–"}</div>
              <div className="mt-1 text-sm font-bold">To: {f.recipient_name || "…"}</div>
              <div className="text-sm">From: {f.buyer_name || "…"}</div>
            </div>
          </div>
        </section>
        <form onSubmit={pay} className="space-y-4 self-start border-2 border-slate-900 bg-white p-5 shadow-[6px_6px_0_0_#0f172a]" data-testid="gift-form">
          <div>
            <div className="font-display text-lg font-extrabold uppercase">Voucher amount</div>
            <div className="mt-2 grid grid-cols-4 gap-2">{PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setAmount(p)} aria-pressed={amount === p} data-testid={`gift-amount-${p}`}
                className={`border-2 py-2 text-sm font-black ${amount === p ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-900"}`}>€{p}</button>))}</div>
            <label className="mt-2 flex items-center gap-2 text-xs font-bold">Or any amount €
              <input type="number" min={20} max={200} step={1} value={amount || ""} onChange={(e) => setAmount(Math.round(Number(e.target.value)))} className="w-24 border-2 border-slate-200 px-2 py-1.5 text-sm font-bold outline-none focus:border-slate-900" data-testid="gift-amount-input" />
              <span className="font-normal text-slate-500">€20–€200</span></label>
          </div>
          <Input label="Your name" required maxLength={80} value={f.buyer_name} onChange={set("buyer_name")} testId="gift-buyer-name" />
          <Input label="Your email (we send the gift card here)" type="email" required value={f.buyer_email} onChange={set("buyer_email")} testId="gift-buyer-email" />
          <Input label="Who's it for?" required maxLength={80} value={f.recipient_name} onChange={set("recipient_name")} testId="gift-recipient-name" />
          <Input label="Their email (optional – we'll email it to them too)" type="email" value={f.recipient_email} onChange={set("recipient_email")} testId="gift-recipient-email" />
          <label className="block text-xs font-bold">Message (optional)<textarea rows={2} maxLength={200} value={f.message} onChange={set("message")} className={field} placeholder="Happy Christmas! Love Granny x" data-testid="gift-message" /></label>
          <button type="submit" disabled={busy || !valid} className="btn-blaze flex w-full items-center justify-center gap-2 px-4 py-3 text-base" data-testid="gift-pay-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Buy {valid ? `€${amount}` : ""} voucher
          </button>
        </form>
      </div>
    </Shell>
  );
}

export function GiftSuccess() {
  usePrivatePage("Gift voucher");
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [s, setS] = useState({ phase: "checking" });
  useEffect(() => {
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/vouchers/status/${sid}`);
        if (data.status === "active") { trackGiftPurchase({ transaction_id: data.code, value: data.amount / 100, items: [{ item_name: "Gift voucher", quantity: 1 }] }); return setS({ phase: "done", data }); }
      } catch (e) { if (e?.response?.status === 404) return setS({ phase: "failed" }); }
      if (++tries >= 15) return setS({ phase: "failed" });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);
  if (s.phase === "checking") return <Shell><div className="py-16 text-center"><Loader2 className="mx-auto h-10 w-10 animate-spin" /><p className="mt-4 font-bold" data-testid="gift-checking">Confirming your payment…</p></div></Shell>;
  if (s.phase === "failed") return <Shell><div className="py-16 text-center"><h1 className="font-display text-3xl font-black uppercase" data-testid="gift-failed">Payment not confirmed</h1>
    <p className="mt-2 text-slate-600">If you were charged, your gift card will arrive by email shortly.</p><Link to="/gift" className="btn-blaze mt-6 inline-block px-5 py-2.5 text-sm">Back to gift vouchers</Link></div></Shell>;
  const { data } = s;
  return (
    <Shell>
      <div className="mx-auto max-w-lg py-8 text-center">
        <GiftIcon className="mx-auto h-14 w-14 text-red-600" />
        <h1 className="font-display mt-3 text-4xl font-black uppercase" data-testid="gift-success">Gift voucher ready!</h1>
        <p className="mt-2 text-slate-600">{eur(data.amount)} for {data.recipient_name}. We've emailed you the gift card{data.recipient_emailed ? ` and sent it to ${data.recipient_name} too` : ""}.</p>
        <div className="mx-auto mt-6 inline-block border-2 border-dashed border-slate-900 bg-lime-50 px-6 py-3 font-mono text-2xl font-black tracking-wider" data-testid="gift-code">{data.code}</div>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a href={`${process.env.REACT_APP_BACKEND_URL}${data.card_url}`} target="_blank" rel="noreferrer" className="btn-blaze flex items-center gap-2 px-5 py-2.5 text-sm" data-testid="gift-card-download"><Download className="h-4 w-4" /> Printable gift card</a>
          <Link to="/gift" className="border-2 border-slate-900 px-5 py-2.5 text-sm font-bold hover:bg-slate-100" data-testid="gift-another">Buy another</Link>
        </div>
      </div>
    </Shell>
  );
}
